import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { Search, Users, TrendingUp, ChevronRight, ChevronLeft, Award, MessageCircle, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const ProfileAvatar = ({ marketer, size = 'sm' }) => {
  const [err, setErr] = useState(false);
  const url = marketer?.profile_image_url
    ? (marketer.profile_image_url.startsWith('/api') ? `${BACKEND_URL}${marketer.profile_image_url}` : marketer.profile_image_url)
    : null;
  const sz = size === 'sm' ? 'w-10 h-10' : 'w-12 h-12';
  if (!url || err) {
    return (
      <div className={`${sz} rounded-full bg-primary-200 flex items-center justify-center flex-shrink-0`}>
        <span className="font-semibold text-primary-800">{marketer?.full_name?.charAt(0).toUpperCase() || '?'}</span>
      </div>
    );
  }
  return <img src={url} alt="" className={`${sz} rounded-full object-cover flex-shrink-0 border-2 border-primary-200`} onError={() => setErr(true)} />;
};

const BORealtors = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [whatsAppLink, setWhatsAppLink] = useState('');
  const [savingWhatsApp, setSavingWhatsApp] = useState(false);

  const fetchData = async (searchQ = '', page = 1) => {
    setSearching(true);
    try {
      const skip = (page - 1) * PAGE_SIZE;
      const res = await boAPI.getRealtors({ search: searchQ || undefined, skip, limit: PAGE_SIZE });
      setData(res.data);
      setCurrentPage(page);
    } catch (e) { toast.error('Failed to load realtors'); }
    finally { setLoading(false); setSearching(false); }
  };

  useEffect(() => { fetchData(); fetchWhatsAppLink(); }, []);

  const fetchWhatsAppLink = async () => {
    try { const res = await boAPI.getWhatsAppGroup(); setWhatsAppLink(res.data.whatsapp_group_link || ''); }
    catch (e) { console.error('fetchWhatsAppLink failed:', e); }
  };

  const handleSaveWhatsApp = async () => {
    setSavingWhatsApp(true);
    try { await boAPI.updateWhatsAppGroup(whatsAppLink); toast.success('WhatsApp group link updated!'); }
    catch (e) { toast.error('Failed to update'); }
    finally { setSavingWhatsApp(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); fetchData(search, 1); };
  const handlePageChange = (p) => fetchData(search, p);

  if (loading) return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" /></div></BOLayout>;

  const { stats, top_recruiters, recent_marketers, realtors, total } = data || {};
  const totalPages = Math.ceil((total || 0) / PAGE_SIZE);

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-realtors-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900">Realtors & Marketers</h1>
            <p className="text-neutral-500 mt-1">Medichi Tribe recruitment network</p>
          </div>
          {/* Tabs */}
          <div className="flex gap-2">
            <button onClick={() => setActiveTab('dashboard')} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeTab === 'dashboard' ? 'bg-primary-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`} data-testid="realtors-dashboard-tab">
              Dashboard
            </button>
            <button onClick={() => { setActiveTab('list'); fetchData(search, 1); }} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeTab === 'list' ? 'bg-primary-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`} data-testid="realtors-list-tab">
              All Marketers
            </button>
          </div>
        </div>

        {activeTab === 'dashboard' ? (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="border-neutral-border shadow-card">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-neutral-500">Total Marketers</p>
                      <p className="text-3xl font-bold text-primary-900">{stats?.total_marketers || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                      <Users className="h-6 w-6 text-primary-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-neutral-border shadow-card">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-neutral-500">Active Recruiters</p>
                      <p className="text-3xl font-bold text-accent-600">{stats?.marketers_with_recruits || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-accent-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-neutral-border shadow-card">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-neutral-500">Hit 50 Target</p>
                      <p className="text-3xl font-bold text-green-700">{(top_recruiters || []).filter(r => (r.recruits_count || 0) >= 50).length}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <Award className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* WhatsApp Group Link Setting */}
            <Card className="border-green-200 shadow-card">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-neutral-900">WhatsApp Group Link</h3>
                    <p className="text-xs text-neutral-500">This link is shown to all marketers on their dashboard</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input value={whatsAppLink} onChange={(e) => setWhatsAppLink(e.target.value)} placeholder="https://chat.whatsapp.com/..." className="flex-1 rounded-xl text-sm" data-testid="bo-whatsapp-link-input" />
                  <Button onClick={handleSaveWhatsApp} disabled={savingWhatsApp} size="sm" className="rounded-xl bg-green-600 hover:bg-green-700" data-testid="bo-save-whatsapp-btn">
                    <Save className="h-4 w-4 mr-1" /> {savingWhatsApp ? '...' : 'Save'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Top Recruiters */}
            <Card className="border-neutral-border shadow-card">
              <CardHeader>
                <CardTitle className="text-xl text-primary-900">Top Recruiters</CardTitle>
                <CardDescription>Marketers with the most recruits</CardDescription>
              </CardHeader>
              <CardContent>
                {top_recruiters && top_recruiters.length > 0 ? (
                  <div className="space-y-3">
                    {top_recruiters.map((r, i) => (
                      <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 hover:bg-neutral-100 transition-colors cursor-pointer" onClick={() => navigate(`/backoffice/realtors/${r.id}`)}>
                        <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                          <span className="font-bold text-sky-700 text-sm">#{i + 1}</span>
                        </div>
                        <ProfileAvatar marketer={r} size="sm" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-primary-900 truncate">{r.full_name}</p>
                          <p className="text-xs text-neutral-500 truncate">{r.location || r.email}</p>
                        </div>
                        <Badge className="bg-primary-100 text-primary-700 text-xs">{r.recruits_count} recruits</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-neutral-500 py-8">No recruiters yet</p>
                )}
              </CardContent>
            </Card>

            {/* Recent Registrations */}
            <Card className="border-neutral-border shadow-card">
              <CardHeader>
                <CardTitle className="text-xl text-primary-900">Recent Registrations</CardTitle>
                <CardDescription>Newly joined marketers</CardDescription>
              </CardHeader>
              <CardContent>
                {recent_marketers && recent_marketers.length > 0 ? (
                  <div className="space-y-3">
                    {recent_marketers.map((m) => (
                      <div key={m.id} className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 hover:bg-neutral-100 transition-colors cursor-pointer" onClick={() => navigate(`/backoffice/realtors/${m.id}`)}>
                        <ProfileAvatar marketer={m} size="sm" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-primary-900 truncate">{m.full_name}</p>
                          <p className="text-xs text-neutral-500 truncate">{m.email}</p>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {m.created_at ? new Date(m.created_at).toLocaleDateString('en-NG') : '--'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-neutral-500 py-8">No marketers yet</p>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            {/* Search */}
            <Card className="border-neutral-border shadow-card">
              <CardContent className="p-5">
                <form onSubmit={handleSearch} className="flex gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                    <Input placeholder="Search by name, email, phone, or location..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-11 pl-10 rounded-xl" data-testid="realtor-search" />
                  </div>
                  <Button type="submit" disabled={searching} className="h-11 px-6 rounded-xl bg-primary-900 hover:bg-primary-800">
                    {searching ? 'Searching...' : 'Search'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* All Marketers List */}
            <Card className="border-neutral-border shadow-card">
              <CardHeader>
                <CardTitle className="text-xl text-primary-900">All Marketers ({total})</CardTitle>
                <CardDescription>
                  Showing {(realtors || []).length} of {total} marketers
                  {totalPages > 1 && ` - Page ${currentPage} of ${totalPages}`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(realtors || []).length > 0 ? (
                  <div className="space-y-3">
                    {realtors.map((m) => (
                      <div key={m.id} className="flex items-start gap-3 p-4 rounded-xl bg-neutral-50 hover:bg-neutral-100 transition-colors cursor-pointer" onClick={() => navigate(`/backoffice/realtors/${m.id}`)}>
                        <ProfileAvatar marketer={m} size="md" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-primary-900 truncate">{m.full_name}</p>
                              <p className="text-sm text-neutral-500 truncate">{m.email}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            <Badge className="bg-primary-100 text-primary-700 text-xs">{m.recruits_count || 0} recruits</Badge>
                            <span className="text-xs text-neutral-400">{m.whatsapp}</span>
                            {m.location && <span className="text-xs text-neutral-400">- {m.location}</span>}
                            <span className="text-xs text-neutral-400">- Joined {m.created_at ? new Date(m.created_at).toLocaleDateString('en-NG') : '--'}</span>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-4 border-t border-neutral-200">
                        <Button variant="outline" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || searching} className="rounded-xl">
                          <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                        </Button>
                        <span className="text-sm text-neutral-500">Page {currentPage} of {totalPages}</span>
                        <Button variant="outline" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || searching} className="rounded-xl">
                          Next <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-neutral-500 py-12">{search ? 'No marketers found matching your search' : 'No marketers registered yet'}</p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </BOLayout>
  );
};

export default BORealtors;
